
import React, { useState, useEffect } from 'react';
import Sidebar from './components/sidebar';
import baseUrl from '../../base_url';
import axios from 'axios';

const AdminProductsListingPage = () => {
  // State management
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  const [variantSearchTerm, setVariantSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Edit Modal State
  const [variantProducts, setVariantProducts] = useState([]); // Full variant objects for display
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    broadCategory: 'Jutexx',
    inventory: '',
    price: '',
    description: '',
    status: "",
    variants: [],
    articleCode: "",
    category : ""
      // This will store only IDs
  });

  const [isSaving, setIsSaving] = useState(false);

  // Product type filters
  const productTypes = [
    'laundry bags' , 'bowl' , 'matka' , 'rect.' , 'ladies side bag' , 'mobile Bags' , 'tissue Boxes'
  ];

  // Fetch products
  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}api/products/data/all-products`);
        setProducts(response.data.data);
        setFilteredProducts(response.data.data);
        setLoading(false);
      } catch (err) {
        console.log("Error while fetching all products");
        console.log(err);
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  // Handle filter change
  useEffect(() => {
    if (selectedFilter === 'All') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product => 
        product.broadCategory === selectedFilter
      );
      setFilteredProducts(filtered);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [selectedFilter, products]);

  // Handle search
  useEffect(() => {
    if (searchTerm) {
      const results = products.filter(product =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(results);
      setCurrentPage(1); // Reset to first page when search changes
    } else if (selectedFilter === 'All') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product => 
        product.broadCategory === selectedFilter
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products, selectedFilter]);

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const maxPagesToShow = 3;
    let pageNumbers = [];
    
    if (totalPages <= maxPagesToShow) {
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Always include page 1
      pageNumbers.push(1);

      if (currentPage <= 2) {
        // Near the start
        pageNumbers.push(2, 3);
        pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 1) {
        // Near the end
        pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Somewhere in the middle
        pageNumbers.push('ellipsis');
        pageNumbers.push(currentPage);
        if (currentPage < totalPages - 1) {
          pageNumbers.push('ellipsis');
          pageNumbers.push(totalPages);
        } else {
          pageNumbers.push(totalPages);
        }
      }
    }
    
    return pageNumbers;
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  
  // Helper function to extract variant IDs
  const extractVariantIds = (variants) => {
    if (!variants) return [];
    return variants.map(variant => 
      typeof variant === 'string' ? variant : variant._id
    );
  };

  // Helper function to find full variant objects by IDs
  const getVariantObjectsFromIds = (variantIds) => {
    return variantIds.map(id => {
      const foundProduct = products.find(p => p._id === id);
      return foundProduct || { _id: id, title: "Unknown Product", image: [] };
    });
  };
  
  // Open edit modal
  const handleEditClick = (product) => {
    setEditingProduct(product);
    
    // Extract just the IDs for the form data
    const variantIds = extractVariantIds(product.variants || []);
    
    setEditFormData({
      title: product.title,
      broadCategory: product.broadCategory,
      inventory: product.inventory,
      price: product.price.replace(/[^0-9]/g, "") / 100,
      description: product.description || "",
      status: product.status,
      variants: variantIds,
      articleCode : product.articleCode,
      category : product.category  
    });
    
    // Store full variant objects for display
    setVariantProducts(getVariantObjectsFromIds(variantIds));
    
    setShowEditModal(true);
  };

  // Close edit modal
  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    setVariantProducts([]);
    setSearchResults([]);
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  const handleVariantSearch = async () => {
    if (!variantSearchTerm.trim()) return;
    
    try {
      setIsSearching(true);
      const response = await axios.get(
        `${baseUrl}api/products/data/search/${encodeURIComponent(variantSearchTerm)}`,
        { withCredentials: true }
      );
      
      // Filter out products that are already selected as variants
      const filteredResults = response.data.data.filter(
        product => !editFormData.variants.includes(product._id)
      );
      setSearchResults(filteredResults);
    } catch (err) {
      console.error("Error searching products:", err);
    } finally {
      setIsSearching(false);
    }
  };

  // Add variant - store ID in form data and full object in display state
  const addVariant = (product) => {
    // Add ID to form data
    setEditFormData(prev => ({
      ...prev,
      variants: [...prev.variants, product._id]
    }));
    
    // Add full object to display state
    setVariantProducts(prev => [...prev, product]);
    
    // Remove from search results
    setSearchResults(searchResults.filter(item => item._id !== product._id));
    setVariantSearchTerm('');
  };

  // Remove variant - remove ID from form data and object from display state
  const removeVariant = (productId) => {
    setEditFormData(prev => ({
      ...prev,
      variants: prev.variants.filter(id => id !== productId)
    }));
    
    setVariantProducts(prev => 
      prev.filter(variant => variant._id !== productId)
    );
  };

  const handleDeleteProduct = async (product) => {
    var id = product._id
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        // Updated to use POST and correct parameters format
        await axios.post(`${baseUrl}api/admin/delete-product/${id}`, {}, {withCredentials: true});
        setProducts(products.filter(user => user._id !== id));
      } catch (err) {
        console.error('Error deleting user:', err);
      }
    }
  };

  // Format price for display
  const formatPrice = (price) => {
    const numericPrice = parseFloat(price.toString().replace(/[^0-9.]/g, "")); // Remove non-numeric characters
    return isNaN(numericPrice)
      ? "Rs.0.00"
      : `Rs.${numericPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Save product changes
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      setIsSaving(true);

      // Identify only the changed fields
      const updatedFields = {};
      Object.keys(editFormData).forEach((key) => {
        // Special handling for variants (compare IDs)
        if (key === "variants") {
          const currentVariantIds = extractVariantIds(editingProduct.variants || []);
          const newVariantIds = editFormData.variants;
          
          // Check if arrays are different
          if (JSON.stringify(currentVariantIds.sort()) !== JSON.stringify([...newVariantIds].sort())) {
            updatedFields.variants = newVariantIds;
          }
        } else {
          const currentValue = key === "price" 
            ? formatPrice(editFormData[key]) 
            : editFormData[key];

          if (currentValue !== (editingProduct[key] || "")) {
            updatedFields[key] = currentValue;
          }
        }
      });

      // If no fields have changed, do nothing
      if (Object.keys(updatedFields).length === 0) {
        setIsSaving(false);
        handleCloseModal();
        return;
      }

      console.log("Updating fields:", updatedFields);

      // API call to update only changed fields
      await axios.post(
        `${baseUrl}api/admin/edit-product/${editingProduct.slug}`,
        updatedFields,
        { withCredentials: true }
      );

      // Update only the modified fields in local state
      const updatedProducts = products.map((product) =>
        product._id === editingProduct._id
          ? { ...product, ...updatedFields }
          : product
      );

      setProducts(updatedProducts);
      handleCloseModal();
      
      // Show success message
      alert("Product updated successfully");
    } catch (err) {
      console.error("Error updating product:", err);
      alert("Failed to update product");
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-700">Products</h1>
            <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded">
              Create Products
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm">
            {/* Tabs */}
            <div className="flex border-b">
              <div className="px-6 py-3 text-blue-600 border-b-2 border-blue-600 font-medium">
                All Products
              </div>
              <div className="px-6 py-3 text-gray-500">
                Unpublish
              </div>
            </div>
            
            {/* Filter and Search */}
            <div className="p-4 border-b">
              <div className="flex">
                <div className="relative">
                  {/* <button 
                    className="bg-gray-200 px-4 py-2 rounded-l flex items-center"
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    <span>Filter: {selectedFilter}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button> */}
                  
                  {/* Dropdown Menu */}
                  {showFilterDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border">
                      {productTypes.map((type) => (
                        <div 
                          key={type} 
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setSelectedFilter(type);
                            setShowFilterDropdown(false);
                          }}
                        >
                          {type}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1 relative">
                  <input 
                    type="text" 
                    placeholder="Search products" 
                    className="w-full rounded-r border border-gray-200 py-2 px-4 pl-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Loading State */}
            {loading ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">Loading products...</p>
              </div>
            ) : (
              <>
                  {/* Table Header */}
                  <div className="grid grid-cols-12 py-3 px-4 border-b text-sm font-medium text-gray-500">
                    <div className="col-span-1">Image</div>
                    <div className="col-span-4">Product</div>
                    <div className="col-span-2">Type</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1">Inventory</div>
                    <div className="col-span-2 text-center">Actions</div>
                  </div>

                  {/* Product Rows */}
                  {currentProducts.length === 0 ? (
                    <div className="p-8 text-center">
                      <p className="text-gray-500">No products match your search or filter.</p>
                    </div>
                  ) : (
                    currentProducts.map(product => (
                      <div key={product.id} className="grid grid-cols-12 py-4 px-4 border-b items-center hover:bg-gray-50">
                        <div className="col-span-1">
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                            <img 
                              src={product.image[0].src} 
                              alt={product.title}
                              className="object-cover"
                            />
                          </div>
                        </div>
                        <a href={`/productpage/${product.slug}`} className="col-span-4 font-medium text-blue-600">{product.title}</a>
                      <div className="col-span-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            product.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}>
                            {product.status || "active"}
                          </span>
                        </div>
                        <div className="col-span-2">{product.category}</div>
                        <div className="col-span-1">{product.inventory}</div>
                        <div className="col-span-2 flex justify-center space-x-2">
                          <button 
                            className="p-1 rounded hover:bg-gray-200"
                            onClick={() => handleEditClick(product)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button className="p-1 rounded hover:bg-gray-200"
                            onClick={() => handleDeleteProduct(product)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                
                {/* Pagination */}
                {filteredProducts.length > 0 && (
                  <div className="flex justify-between items-center p-4 text-sm">
                    <div className="text-gray-600">
                      Showing {indexOfFirstProduct + 1} to {Math.min(indexOfLastProduct, filteredProducts.length)} of {filteredProducts.length} products
                    </div>
                    <div className="flex items-center">
                      <button 
                        className={`p-1 rounded mx-1 ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-500'}`}
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </button>
                      <button 
                        className={`p-1 rounded mx-1 ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-500'}`}
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      {/* Page Numbers with Ellipsis */}
                      <div className="flex mx-2">
                        {getPageNumbers().map((pageNumber, index) => (
                          pageNumber === 'ellipsis' ? (
                            <span key={`ellipsis-${index}`} className="w-8 h-8 flex items-center justify-center">...</span>
                          ) : (
                            <button
                              key={pageNumber}
                              onClick={() => paginate(pageNumber)}
                              className={`w-8 h-8 mx-1 rounded-full ${
                                currentPage === pageNumber 
                                  ? 'bg-blue-600 text-white' 
                                  : 'text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          )
                        ))}
                      </div>
                      
                      <button 
                        className={`p-1 rounded mx-1 ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-500'}`}
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <button 
                        className={`p-1 rounded mx-1 ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-500'}`}
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Edit Product Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">Edit Product</h3>
              <button 
                className="text-gray-500 hover:text-gray-700"
                onClick={handleCloseModal}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSaveChanges} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Product Title */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={editFormData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    
                  />
                </div>
                
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    name="category"
                    value={editFormData.category}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    
                  >
                    {productTypes.filter(type => type !== 'All').map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                {/* Inventory */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inventory
                  </label>
                  <input
                    type="number"
                    name="inventory"
                    value={editFormData.inventory}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    
                  />
                </div>
                
                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <input
                    type="Number"
                    name="price"
                    value={editFormData.price}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    articleCode
                  </label>
                  <input
                    type="text"
                    name="articleCode"
                    value={editFormData.articleCode}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Active Status
                  </label>
                  <select
                    name="status"
                    value={editFormData.status}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                                
                {/* Product Images (Placeholder for now) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Images
                  </label>
                  <div className="flex items-center space-x-2">
                    {editingProduct?.image?.slice(0, 3).map((img, idx) => (
                      <div key={idx} className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                        <img 
                          src={img.dataAltImage} 
                          alt={`Product ${idx}`}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    ))}
                    <button 
                      type="button"
                      className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:text-gray-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={editFormData.description}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>

                {/* Variants */}
                <div className="md:col-span-2 mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Variants
                  </label>
                  
                  {/* Search for products */}
                  <div className="flex mb-2">
                    <input
                      type="text"
                      placeholder="Search products to add as variants"
                      value={variantSearchTerm}
                      onChange={(e) => setVariantSearchTerm(e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleVariantSearch}
                      disabled={isSearching}
                      className="px-4 py-2 bg-blue-600 text-white rounded-r hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isSearching ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  
                  {/* Search results */}
                  {searchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded mb-4">
                      <ul className="divide-y divide-gray-200">
                        {searchResults.map(product => (
                          <li 
                            key={product._id} 
                            className="p-2 hover:bg-gray-50 flex justify-between items-center"
                          >
                            <div className="flex items-center">
                              {product.image && product.image[0] && (
                                <img 
                                  src={product.image[0].dataAltImage} 
                                  alt={product.title}
                                  className="w-8 h-8 mr-2 object-cover"
                                />
                              )}
                              <a href={`/productpage/${product.slug}`} target='_blank' rel='noreferrer' className="truncate">{product.title}</a>
                            </div>
                            <button
                              type="button"
                              onClick={() => addVariant(product)}
                              className="ml-2 text-sm px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                            >
                              Add
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Selected variants */}
                  <div className="border border-gray-200 rounded p-2">
                    <h4 className="text-sm font-medium mb-2">Selected Variants</h4>
                      {variantProducts.length === 0 ? (
                        <p className="text-gray-500 text-sm italic">No variants selected</p>
                      ) : (
                        <ul className="space-y-2">
                          {variantProducts.map(variant => (
                            <li 
                              key={variant._id} 
                              className="flex justify-between items-center p-2 bg-gray-50 rounded"
                            >
                              <div className="flex items-center">
                                {variant.image && variant.image[0] && (
                                  <img 
                                    src={variant.image[0].dataAltImage} 
                                    alt={variant.title}
                                    className="w-8 h-8 mr-2 object-cover"
                                  />
                                )}
                                <span className="text-sm">{variant.title}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeVariant(variant._id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              </div>
              
              <div className="mt-8 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`px-4 py-2 bg-blue-600 text-white rounded ${isSaving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsListingPage;